import type { Tables } from '../../types/database.types';

export type SupplierOrganization = Tables<'supplier_organizations'>;

/**
 * The minimum safe organization details a supplier user may see about their
 * own organization. Administrative fields (contact_email, created_by,
 * timestamps) are TAP Admin concerns and are never selected for suppliers.
 */
export interface OwnSupplierOrganization {
  id: string;
  name: string;
}

export interface SupplierOrganizationInput {
  name: string;
  code: string;
  contactEmail: string;
}

/**
 * The operational half of a factory's record.
 *
 * Separate from SupplierOrganizationInput because it is written by a different
 * path: name, code and contact email go through an ordinary RLS-guarded
 * update, and these three go through update_supplier_profile, which is
 * admin-only at the database. Merging them would mean one of the two sets
 * quietly inherits the other's permission.
 */
export interface SupplierProfileInput {
  contactName: string;
  address: string;
  /**
   * Two windows, because a factory makes two promises: days to send samples
   * after accepting the PO, and days to have the order ready once those
   * samples are approved. Null on either means "no factory default — use the
   * workspace number".
   */
  defaultSampleDueDays: number | null;
  defaultProductionDays: number | null;
}

/** Client-side mirror of supplier_organizations_code_format. */
export const CODE_PATTERN = /^[a-z0-9]([a-z0-9-]{0,30}[a-z0-9])?$/;

/** Lowercases and trims a code so typing "ACME Corp " still submits cleanly. */
export function normalizeCode(raw: string): string {
  return raw.trim().toLowerCase().replace(/\s+/g, '-');
}

export function isValidCode(code: string): boolean {
  return code.length >= 2 && CODE_PATTERN.test(code);
}
